import { Icon } from 'framework7-react';
import { useRef } from 'react';
import type { RestaurantAccessState } from '@restaurant/api-client';
import { useT } from '@/i18n';
import { unregisterPush } from '@/features/notifications/hooks/usePushRegistration';
import { useAuthStore } from '@/stores/auth.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { telHref, whatsAppHref } from './contact-links';
import './suspension-screen.css';

interface SuspensionScreenProps {
  state: RestaurantAccessState;
  restaurantName: string;
  checking: boolean;
  onCheckAgain: () => void;
}

export function SuspensionScreen({
  state,
  restaurantName,
  checking,
  onCheckAgain,
}: SuspensionScreenProps) {
  const t = useT();
  const s = t.suspension;
  const logout = useAuthStore((st) => st.logout);
  const clearRestaurant = useRestaurantStore((st) => st.clear);
  const signingOutRef = useRef(false);

  const phone = state.contactPhone;
  const tel = phone ? telHref(phone) : null;
  const whatsApp = phone ? whatsAppHref(phone) : null;

  const signOut = async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    await unregisterPush();
    logout();
    clearRestaurant();
  };

  return (
    <div
      className="suspension-screen"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="suspension-heading"
      aria-describedby="suspension-body"
      data-testid="suspension-screen"
    >
      <div className="suspension-card">
        <div className="suspension-badge" aria-hidden="true">
          <Icon ios="f7:lock_fill" md="material:lock" />
        </div>
        {restaurantName && <p className="suspension-venue">{restaurantName}</p>}
        <h1 id="suspension-heading" className="suspension-heading">
          {s.heading}
        </h1>
        <p id="suspension-body" className="suspension-body">
          {s.body}
        </p>

        {state.reason && (
          <div className="suspension-reason" data-testid="suspension-reason">
            <span className="suspension-label">{s.reasonLabel}</span>
            <p>{state.reason}</p>
          </div>
        )}

        {phone && (
          <div className="suspension-contact">
            <span className="suspension-label">{s.contactLabel}</span>
            {tel ? (
              <a className="suspension-phone" href={tel} data-testid="suspension-phone">
                {phone}
              </a>
            ) : (
              <span className="suspension-phone" data-testid="suspension-phone">
                {phone}
              </span>
            )}
            <div className="suspension-actions">
              {whatsApp && (
                <a
                  className="suspension-action suspension-action--whatsapp"
                  href={whatsApp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="suspension-whatsapp"
                >
                  <Icon ios="f7:chat_bubble_fill" md="material:chat" />
                  {s.whatsApp}
                </a>
              )}
            </div>
          </div>
        )}

        <div className="suspension-footer">
          <button
            type="button"
            className="suspension-link"
            onClick={onCheckAgain}
            disabled={checking}
            data-testid="suspension-check-again"
          >
            {checking ? t.loading : s.checkAgain}
          </button>
          <span className="suspension-dot" aria-hidden="true">
            ·
          </span>
          <button
            type="button"
            className="suspension-link"
            onClick={signOut}
            data-testid="suspension-sign-out"
          >
            {t.signOut}
          </button>
        </div>
      </div>
    </div>
  );
}
